/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import { ResultAsync } from "@/utils/result";

export interface RequestError {
    error: string | Error
    status?: number
}

const ajaxHeaderName = 'X-Requested-With';
const ajaxHeaderValue = 'XMLHttpRequest';

export function addAjaxHeader(headers?: HeadersInit): HeadersInit {
    if (!headers) {
        headers = new Headers();
    }

    if (headers instanceof Headers) {
        headers.append(ajaxHeaderName, ajaxHeaderValue);
    }
    else if (Array.isArray(headers)) {
        headers.push([ajaxHeaderName, ajaxHeaderValue]);
    }
    else {
        headers[ajaxHeaderName] = ajaxHeaderValue;
    }

    return headers;
}

/**
 * Performs a fetch request to the server, parsing the response as json.
 */
export function requestJson<T>(
    resource: string | URL | Request,
    options?: RequestInit
): ResultAsync<T, RequestError> {

    if (resource instanceof Request) {
        addAjaxHeader(resource.headers);
    } else {
        options = options || {};
        options.headers = addAjaxHeader(options.headers);
    }

    return request(resource, options)
        .map<T>(response => response.json());
}

/**
 * Perform a fetch request to the server, parsing the response as text.
 */
export function requestText(
    resource: string | URL | Request,
    options?: RequestInit
): ResultAsync<string, RequestError> {
    return request(resource, options)
        .map(response => response.text());
}

/**
 * Perform a fetch request to the server.
 */
export function request(
    resource: string | URL | Request,
    options?: RequestInit
): ResultAsync<Response, RequestError> {
    return ResultAsync.fromPromise(
        fetch(resource, options)
            .then(response => response.ok
                ? response
                : Promise.reject(response)
            ),
        (error) => error instanceof Response
            ? error
            : { error: error as Error }
    )
    .mapErr<RequestError>(error => error instanceof Response
        ? processErrorResponse(error)
        : error
    );
}

function processErrorResponse(error: Response): Promise<RequestError> {
    return error.text()
        .then(parseResponseHtml)
        .then(text => text.trim())
        .then(text => ({
            status: error.status,
            error: new Error(text)
        }));
}

export function parseResponseHtml(text: string): string {
    const parser = new DOMParser();
    return parser.parseFromString(text, 'text/html').body.textContent;
}
